import { useRuntimeConfig } from '#imports'

/**
 * Origem Flow: integração com o Google Drive via Google Picker.
 *
 * Fornece `$googleDrive` com:
 * - enabled: se o client id foi configurado (NUXT_PUBLIC_GOOGLE_CLIENT_ID);
 * - pickFiles(): abre o picker e resolve com os arquivos escolhidos;
 * - downloadFile(doc): baixa o conteúdo de um arquivo escolhido (exporta
 *   Docs/Sheets/Slides nativos para o formato Office equivalente).
 *
 * Usa o escopo `drive.file` (não sensível): o app só acessa os arquivos que
 * o próprio usuário escolher no picker — nada além disso.
 */

const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file'

// Exportações para formatos nativos do Google (Docs/Sheets/Slides…).
const GOOGLE_EXPORTS = {
  'application/vnd.google-apps.document': {
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extension: '.docx',
  },
  'application/vnd.google-apps.spreadsheet': {
    mimeType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: '.xlsx',
  },
  'application/vnd.google-apps.presentation': {
    mimeType:
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    extension: '.pptx',
  },
  'application/vnd.google-apps.drawing': {
    mimeType: 'image/png',
    extension: '.png',
  },
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.onload = resolve
    script.onerror = () => reject(new Error(`Falha ao carregar ${src}`))
    document.head.appendChild(script)
  })
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig().public
  const clientId = config.googleClientId || ''
  const apiKey = config.googleApiKey || ''
  // O "app id" do picker é o número do projeto (prefixo do client id).
  const appId = clientId.split('-')[0] || ''

  let accessToken = null
  let accessTokenExpiresAt = 0
  let pickerReady = false

  async function ensureLibraries() {
    await Promise.all([
      loadScript('https://accounts.google.com/gsi/client'),
      loadScript('https://apis.google.com/js/api.js'),
    ])
    if (!pickerReady) {
      await new Promise((resolve, reject) => {
        window.gapi.load('picker', {
          callback: resolve,
          onerror: () => reject(new Error('Falha ao carregar o Google Picker')),
        })
      })
      pickerReady = true
    }
  }

  function requestAccessToken() {
    if (accessToken && Date.now() < accessTokenExpiresAt - 30000) {
      return Promise.resolve(accessToken)
    }
    return new Promise((resolve, reject) => {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: DRIVE_SCOPE,
        callback: (response) => {
          if (response.error) {
            reject(new Error(response.error))
            return
          }
          accessToken = response.access_token
          accessTokenExpiresAt =
            Date.now() + parseInt(response.expires_in || 3600, 10) * 1000
          resolve(accessToken)
        },
        error_callback: (error) => {
          reject(new Error(error?.type || 'popup_failed'))
        },
      })
      tokenClient.requestAccessToken()
    })
  }

  async function pickFiles({ multiselect = true } = {}) {
    if (!clientId) {
      throw new Error('Google Drive não configurado')
    }
    await ensureLibraries()
    const token = await requestAccessToken()

    return await new Promise((resolve) => {
      // Aba padrão: lista simples de arquivos (DocsView com pastas pode
      // renderizar vazia em algumas contas); pastas ficam na segunda aba.
      const filesView = new window.google.picker.DocsView(
        window.google.picker.ViewId.DOCS
      )
      const foldersView = new window.google.picker.DocsView(
        window.google.picker.ViewId.DOCS
      )
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)

      let builder = new window.google.picker.PickerBuilder()
        .addView(filesView)
        .addView(foldersView)
        .setOAuthToken(token)
        .setLocale('pt-BR')
        .setTitle('Google Drive')
        .setCallback((data) => {
          const Action = window.google.picker.Action
          if (data.action === Action.PICKED) {
            resolve(
              (data.docs || []).map((doc) => ({
                id: doc.id,
                name: doc.name,
                mimeType: doc.mimeType,
                url: doc.url,
                sizeBytes: doc.sizeBytes || null,
              }))
            )
          } else if (data.action === Action.CANCEL) {
            resolve([])
          }
        })
      if (appId) {
        builder = builder.setAppId(appId)
      }
      if (apiKey) {
        builder = builder.setDeveloperKey(apiKey)
      }
      if (multiselect) {
        builder = builder.enableFeature(
          window.google.picker.Feature.MULTISELECT_ENABLED
        )
      }
      builder.build().setVisible(true)
    })
  }

  /**
   * Baixa o conteúdo de um arquivo escolhido no picker e devolve um File
   * pronto para upload no Origem Flow.
   */
  async function downloadFile(doc) {
    const token = await requestAccessToken()
    const isNative = doc.mimeType?.startsWith('application/vnd.google-apps')
    let url
    let name = doc.name
    let mimeType = doc.mimeType
    if (isNative) {
      const exportInfo = GOOGLE_EXPORTS[doc.mimeType]
      if (!exportInfo) {
        // Formato nativo sem exportação mapeada (ex.: Forms) — sem download.
        return null
      }
      mimeType = exportInfo.mimeType
      if (!name.toLowerCase().endsWith(exportInfo.extension)) {
        name += exportInfo.extension
      }
      url = `https://www.googleapis.com/drive/v3/files/${doc.id}/export?mimeType=${encodeURIComponent(mimeType)}`
    } else {
      url = `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`
    }
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) {
      throw new Error(`Download do Drive falhou (${response.status})`)
    }
    const blob = await response.blob()
    return new File([blob], name, { type: mimeType || blob.type })
  }

  nuxtApp.provide('googleDrive', {
    get enabled() {
      return clientId !== ''
    },
    pickFiles,
    downloadFile,
  })
})
